/**
 * The command shell's fixed widget set (brief §9.3): one widget per kind the
 * request schema can describe (`widgets.ts` decides which), each bound to its
 * body path, its errors shown beneath it with `aria-invalid` /
 * `aria-describedby` (api-validation.md). The table, address and entity
 * widgets have files of their own.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useObjectUiAdapter } from "@shared-core/data/ObjectUiAdapterContext";
import {
  isChosenFile,
  type FieldError,
} from "@shared-core/domain/object-ui/contract";
import { currentTime } from "@shared-core/domain/current-time";
import { useObjectManifest } from "@shared-core/domain/object-ui/manifest";
import { emptyListQuery } from "@shared-core/domain/object-ui/list-query";
import {
  instantOfLocalDateTime,
  localDateTimeInputValue,
} from "@shared-core/domain/object-ui/values";
import {
  pickedChoiceValue,
  resolveDateBound,
  type FieldDescriptor,
} from "@shared-core/domain/object-ui/widgets";
import { Checkbox } from "../../primitives/checkbox";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Textarea } from "../../primitives/textarea";
import { cn } from "../../primitives/utils";
import { MultiSelect } from "./MultiSelect";
import { TagListInput } from "./TagListInput";
import { PlainFileField } from "./pasteScreenshot";
import { UploadField, UploadFilesField } from "./UploadField";

export interface FieldChange {
  (value: unknown, options?: { debounce?: boolean }): void;
}

export interface SimpleFieldProps {
  field: FieldDescriptor;
  value: unknown;
  errors: FieldError[];
  disabled: boolean;
  onChange: FieldChange;
  /** The dry run's `field_hints[path].note`: one sentence under the field, worded by the backend. */
  note?: string | null;
}

/** A DOM id for a body path. */
export function fieldId(path: string): string {
  return `cmd-${path.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
}

/** The label, the input, the errors beneath. */
export function FieldFrame({
  id,
  label,
  required,
  errors,
  children,
  hint,
  asGroup = false,
}: {
  id: string;
  label: string;
  required: boolean;
  errors: FieldError[];
  children: ReactNode;
  hint?: ReactNode;
  /** A group of inputs (radio, table) is labelled, not a single control. */
  asGroup?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      {asGroup ? (
        <p id={`${id}-label`} className="text-sm font-medium">
          {label}
          {!required && (
            <span className="ml-1 font-normal text-muted-foreground">
              (optional)
            </span>
          )}
        </p>
      ) : (
        <Label htmlFor={id}>
          {label}
          {!required && (
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          )}
        </Label>
      )}
      {children}
      {hint}
      {errors.length > 0 && (
        <p id={`${id}-error`} className="text-sm text-red-600">
          {errors.map((e) => e.message).join(" ")}
        </p>
      )}
    </div>
  );
}

export function invalidProps(
  id: string,
  errors: FieldError[],
  describedBy: readonly string[] = [],
) {
  const ids = [errors.length > 0 ? `${id}-error` : null, ...describedBy]
    .filter(Boolean)
    .join(" ");
  return {
    ...(errors.length > 0 ? { "aria-invalid": true } : {}),
    ...(ids ? { "aria-describedby": ids } : {}),
  };
}

function asText(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}

/** Pence in the body, pounds in the box; the box keeps what was typed. */
function MoneyInput({
  id,
  value,
  disabled,
  onChange,
  errors,
  className,
  ariaLabel,
}: {
  id: string;
  value: unknown;
  disabled: boolean;
  onChange: FieldChange;
  errors: FieldError[];
  className?: string;
  ariaLabel?: string;
}) {
  const fromValue = (v: unknown) =>
    typeof v === "number" ? (v / 100).toFixed(2) : "";
  const [text, setText] = useState(() => fromValue(value));
  // A value set from outside (a prefill, "set to max") replaces the box.
  useEffect(() => {
    setText((current) => {
      const typed = Math.round(Number(current) * 100);
      return typeof value === "number" && typed === value
        ? current
        : fromValue(value);
    });
  }, [value]);
  return (
    <div className={cn("flex items-center gap-1", className)}>
      <span className="text-muted-foreground" aria-hidden>
        £
      </span>
      <Input
        id={id}
        inputMode="decimal"
        className="tabular-nums"
        value={text}
        disabled={disabled}
        aria-label={ariaLabel}
        {...invalidProps(id, errors)}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          const pounds = Number(next.replace(/[£,\s]/g, ""));
          onChange(
            next.trim() === "" || !Number.isFinite(pounds)
              ? null
              : Math.round(pounds * 100),
            { debounce: true },
          );
        }}
      />
    </div>
  );
}

export { MoneyInput };

/** What a locked value reads as: an option's label where the field offers options, else the value. */
function lockedText(field: FieldDescriptor, value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(", ");
  if (field.kind === "select" || field.kind === "radio") {
    const option = field.options.find((o) => o.value === String(value));
    if (option) return option.label;
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return asText(value);
}

/**
 * A field whose value came from the record the command was opened from (a
 * note's subject): shown with its label, never editable. Whatever the
 * field's own widget, the person is told, not asked.
 */
export function LockedField({
  field,
  value,
  errors,
}: Pick<SimpleFieldProps, "field" | "value" | "errors">) {
  const id = fieldId(field.path);
  return (
    <FieldFrame
      id={id}
      label={field.label}
      required={field.required}
      errors={errors}
      hint={
        <p id={`${id}-locked`} className="text-xs text-muted-foreground">
          Set by the record you opened the form from.
        </p>
      }
    >
      <Input
        id={id}
        readOnly
        value={lockedText(field, value)}
        aria-describedby={`${id}-locked`}
      />
    </FieldFrame>
  );
}

/**
 * What a typed text box sends: an optional field left empty (or cleared) sends
 * nothing, so its format is never judged on a blank. A required one sends the
 * blank, and the dry run asks for it.
 */
export function typedText(
  field: { required: boolean },
  raw: string,
): string | null {
  return !field.required && raw.trim() === "" ? null : raw;
}

/** One of the simple fixed widgets (not table, address or entity). */
export function SimpleField({
  field,
  value,
  errors,
  disabled,
  onChange,
  note = null,
}: SimpleFieldProps) {
  const id = fieldId(field.path);
  const noteId = `${id}-note`;
  const frame = (control: ReactNode, asGroup = false, hint?: ReactNode) => (
    <FieldFrame
      id={id}
      label={field.label}
      required={field.required}
      errors={errors}
      asGroup={asGroup}
      hint={
        <>
          {hint}
          {note && (
            <p id={noteId} className="text-xs text-muted-foreground">
              {note}
            </p>
          )}
        </>
      }
    >
      {control}
    </FieldFrame>
  );
  const described = (...ids: string[]) =>
    invalidProps(id, errors, note ? [...ids, noteId] : ids);
  switch (field.kind) {
    case "text":
      return frame(
        <Input
          id={id}
          placeholder={field.placeholder}
          value={asText(value)}
          disabled={disabled}
          {...described()}
          onChange={(e) =>
            onChange(typedText(field, e.target.value), { debounce: true })
          }
        />,
      );
    case "textarea":
      return frame(
        <Textarea
          id={id}
          placeholder={field.placeholder}
          value={asText(value)}
          disabled={disabled}
          {...described()}
          onChange={(e) =>
            onChange(typedText(field, e.target.value), { debounce: true })
          }
        />,
      );
    case "formatted_text": {
      // Plain text box, one line saying what to type: no preview and no toolbar (ruling 27, not a bespoke editor).
      const hintId = `${id}-format-hint`;
      return frame(
        <Textarea
          id={id}
          placeholder={field.placeholder}
          value={asText(value)}
          disabled={disabled}
          {...described(hintId)}
          onChange={(e) =>
            onChange(typedText(field, e.target.value), { debounce: true })
          }
        />,
        false,
        <p id={hintId} className="text-xs text-muted-foreground">
          {field.formatHint}
        </p>,
      );
    }
    case "number":
      return frame(
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          className="max-w-40 tabular-nums"
          value={asText(value)}
          disabled={disabled}
          {...described()}
          onChange={(e) =>
            onChange(e.target.value === "" ? null : Number(e.target.value), {
              debounce: true,
            })
          }
        />,
      );
    case "money":
      return frame(
        <MoneyInput
          id={id}
          className="max-w-48"
          value={value}
          disabled={disabled}
          errors={errors}
          onChange={onChange}
        />,
      );
    case "date": {
      const today = currentTime();
      return frame(
        <Input
          id={id}
          type="date"
          className="max-w-48"
          value={asText(value)}
          min={resolveDateBound(field.min, today)}
          disabled={disabled}
          {...described()}
          onChange={(e) => onChange(e.target.value || null)}
        />,
      );
    }
    case "datetime":
      return frame(
        <Input
          id={id}
          type="datetime-local"
          className="max-w-56"
          value={localDateTimeInputValue(value)}
          disabled={disabled}
          {...described()}
          onChange={(e) => onChange(instantOfLocalDateTime(e.target.value))}
        />,
      );
    case "checkbox":
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Checkbox
              id={id}
              checked={value === true}
              disabled={disabled}
              {...described()}
              onCheckedChange={(checked) => onChange(checked === true)}
            />
            <Label htmlFor={id} className="font-normal">
              {field.label}
            </Label>
          </div>
          {note && (
            <p id={noteId} className="text-xs text-muted-foreground">
              {note}
            </p>
          )}
          {errors.length > 0 && (
            <p id={`${id}-error`} className="text-sm text-red-600">
              {errors.map((e) => e.message).join(" ")}
            </p>
          )}
        </div>
      );
    case "select":
      // One option is nothing to choose: shown as text, its value already in the body (`soleOptionValues`).
      if (field.options.length === 1)
        return frame(
          <Input id={id} readOnly value={field.options[0]!.label} />,
        );
      return frame(
        <Select
          value={asText(value) || undefined}
          disabled={disabled}
          onValueChange={(next) => onChange(pickedChoiceValue(field, next))}
        >
          <SelectTrigger id={id} className="w-full max-w-md" {...described()}>
            <SelectValue placeholder="Choose…" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={!!option.disabled_reason}
              >
                {option.label}
                {option.disabled_reason && (
                  <span className="text-xs text-muted-foreground">
                    {" "}
                    · {option.disabled_reason}
                  </span>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>,
      );
    case "radio":
      return frame(
        <RadioGroup
          aria-labelledby={`${id}-label`}
          value={asText(value)}
          disabled={disabled}
          onValueChange={(next) => onChange(pickedChoiceValue(field, next))}
          {...described()}
        >
          {field.options.map((option) => {
            const optionId = `${id}-${option.value}`;
            return (
              <div key={option.value} className="flex items-start gap-2">
                <RadioGroupItem
                  id={optionId}
                  value={option.value}
                  disabled={!!option.disabled_reason}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={optionId}
                  className={cn(
                    "flex-col items-start gap-0 font-normal",
                    option.disabled_reason && "text-muted-foreground",
                  )}
                >
                  <span>{option.label}</span>
                  {option.disabled_reason && (
                    <span className="text-xs">{option.disabled_reason}</span>
                  )}
                </Label>
              </div>
            );
          })}
        </RadioGroup>,
        true,
      );
    case "multiselect":
      return frame(
        <MultiSelect
          id={id}
          label={field.label}
          options={field.options}
          value={Array.isArray(value) ? value.map(String) : []}
          disabled={disabled}
          invalid={described()}
          onChange={(next) => onChange(next)}
        />,
        true,
      );
    case "taglist":
      return frame(
        <TagListInput
          id={id}
          label={field.label}
          itemKind={field.itemKind}
          value={
            Array.isArray(value)
              ? value.filter(
                  (v): v is string | number =>
                    typeof v === "string" || typeof v === "number",
                )
              : []
          }
          disabled={disabled}
          invalid={described()}
          onChange={(next) => onChange(next)}
        />,
      );
    case "upload":
      return frame(
        field.multiple ? (
          <UploadFilesField
            id={id}
            upload={field.upload}
            accept={field.accept}
            maxBytes={field.maxBytes}
            maxFiles={field.maxFiles}
            value={
              Array.isArray(value)
                ? value.filter((v): v is string => typeof v === "string")
                : []
            }
            disabled={disabled}
            invalid={described()}
            onChange={(refs) => onChange(refs)}
          />
        ) : (
          <UploadField
            id={id}
            upload={field.upload}
            accept={field.accept}
            maxBytes={field.maxBytes}
            value={typeof value === "string" && value ? value : null}
            disabled={disabled}
            invalid={described()}
            onChange={(ref) => onChange(ref)}
          />
        ),
      );
    case "file":
      return frame(
        <PlainFileField
          id={id}
          value={isChosenFile(value) ? value : null}
          disabled={disabled}
          invalid={described()}
          onChange={onChange}
        />,
      );
    case "suggest":
      return (
        <SuggestField
          field={field}
          value={value}
          errors={errors}
          disabled={disabled}
          onChange={onChange}
          note={note}
        />
      );
    default:
      return null;
  }
}

/** Free text with suggestions from an object's list, searched as the person types. */
function SuggestField({
  field,
  value,
  errors,
  disabled,
  onChange,
  note = null,
}: SimpleFieldProps) {
  const adapter = useObjectUiAdapter();
  const index = useObjectManifest();
  const id = fieldId(field.path);
  const objectId = field.kind === "suggest" ? field.object : null;
  const object =
    objectId && index.isObjectEnabled(objectId)
      ? index.objectOf(objectId)
      : null;
  const text = asText(value);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  useEffect(() => {
    const list = object?.list;
    if (!list) return;
    let isCurrent = true;
    const handle = setTimeout(() => {
      adapter
        .list(list, {
          ...emptyListQuery(),
          q: text.trim() || null,
          page_size: 6,
        })
        .then((page) => {
          if (!isCurrent) return;
          setSuggestions(
            page.items
              .map((item) => String(item[object.identifier.field] ?? ""))
              .filter((s) => s && s !== text),
          );
        })
        .catch(() => {
          if (isCurrent) setSuggestions([]);
        });
    }, 200);
    return () => {
      isCurrent = false;
      clearTimeout(handle);
    };
  }, [adapter, object, text]);
  return (
    <FieldFrame
      id={id}
      label={field.label}
      required={field.required}
      errors={errors}
      hint={
        <>
          {suggestions.length > 0 && !disabled && (
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="h-6 rounded-full border border-border bg-card px-2 text-xs hover:bg-accent"
                  onClick={() => onChange(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {note && (
            <p id={`${id}-note`} className="text-xs text-muted-foreground">
              {note}
            </p>
          )}
        </>
      }
    >
      <Input
        id={id}
        value={text}
        disabled={disabled}
        {...invalidProps(id, errors, note ? [`${id}-note`] : [])}
        onChange={(e) =>
          onChange(typedText(field, e.target.value), { debounce: true })
        }
      />
    </FieldFrame>
  );
}
